import React, { useState, useEffect, useCallback } from 'react';
import { getTranslation, getRandomTopicExample, getKnowledgeLevels } from './languages';
import { getTimezoneOptions, getTimezoneName } from './ai/enums';
import { useTimezone } from './contexts/TimezoneContext';
import { API_ENDPOINTS } from './config';
import { renderMarkdown } from './utils/markdownUtils';

const learningPurposes = [
    { id: 'hobby', labelKey: 'hobby' },
    { id: 'professional', labelKey: 'professional' }
];

const languageOptions = [
    { id: 'en', label: 'English' },
    { id: 'zh-CN', label: 'Simplified Chinese (简体中文)' },
    { id: 'zh-Hant', label: 'Traditional Chinese (繁體中文)' },
    { id: 'ja', label: 'Japanese (日本語)' }
];

// Map UI language codes to language option IDs
const getDefaultLanguage = (uiLanguage) => {
    switch (uiLanguage) {
        case 'zh-CN':
            return 'zh-CN';
        case 'zh-Hant':
            return 'zh-Hant';
        case 'ja':
            return 'ja';
        case 'en':
        default:
            return 'en';
    }
};

export default function EditTopicModal({ isOpen, onClose, topic, onUpdateTopic, onDeleteTopic, uiLanguage = 'en', authenticatedFetch, hasPendingSummaryRequest, addPendingRequest, removePendingRequest, generateRequestId, tutorialStepId }) {
    const [topicName, setTopicName] = useState('');
    const [userScale, setUserScale] = useState(3);
    const [purpose, setPurpose] = useState('hobby');
    const [language, setLanguage] = useState(getDefaultLanguage(uiLanguage));
    const [description, setDescription] = useState('');
    const [learningSummary, setLearningSummary] = useState('');
    const [includeSummaryInContext, setIncludeSummaryInContext] = useState(true);
    const [localGenerationComplete, setLocalGenerationComplete] = useState(false);
    const [locallyGeneratedSummary, setLocallyGeneratedSummary] = useState(null);
    const [isLocallyGenerating, setIsLocallyGenerating] = useState(false);
    
    // Use pending request state for immediate UI feedback
    // The button should be disabled only when there's an active request
    const isGeneratingSummary = (hasPendingSummaryRequest && hasPendingSummaryRequest(topic?.id)) || false;
    
    const [summaryUnavailableReason, setSummaryUnavailableReason] = useState(null);
    const [summaryMessage, setSummaryMessage] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [knowledgeSubTab, setKnowledgeSubTab] = useState(0); // 0 = Learning Context & Goals, 1 = AI Learning Summary
    const [topicExample, setTopicExample] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isScheduled, setIsScheduled] = useState(false);
    const [scheduleTimeHour, setScheduleTimeHour] = useState(7);
    const [scheduleTimePeriod, setScheduleTimePeriod] = useState(0); // 0 = AM, 1 = PM
    const [scheduleFrequencyValue, setScheduleFrequencyValue] = useState(1);
    const [scheduleFrequencyUnit, setScheduleFrequencyUnit] = useState(1); // 0 = hours, 1 = days, 2 = weeks, 3 = months
    const [scheduledSkipIfUnread, setScheduledSkipIfUnread] = useState(false);
    const { timezone } = useTimezone();
    const [userTimezone, setUserTimezone] = useState(timezone);
    const [savedTimezone, setSavedTimezone] = useState(null);
    const [showTimezoneAlert, setShowTimezoneAlert] = useState(false);
    const [wasHoursInDev, setWasHoursInDev] = useState(false);

    const t = useCallback((key) => getTranslation(key, uiLanguage), [uiLanguage]);
    
    // Note: renderMarkdown is now imported from utils/markdownUtils.js
    
    // Get translated knowledge levels
    const getTranslatedKnowledgeLevels = () => {
        const levels = [];
        const knowledgeLevels = getKnowledgeLevels(uiLanguage);
        for (let i = 0; i <= 10; i++) {
            const levelData = knowledgeLevels[i.toString()];
            if (levelData) {
                levels.push({
                    scale: i,
                    label: levelData.label,
                    description: levelData.description,
                    hobbyDescription: levelData.hobbyDescription,
                    professionalDescription: levelData.professionalDescription
                });
            }
        }
        return levels;
    };
    
    const translatedKnowledgeLevels = getTranslatedKnowledgeLevels();
    const currentLevel = translatedKnowledgeLevels.find(l => l.scale === userScale) || {};
    
    // Get the appropriate description based on the selected purpose
    const getCurrentDescription = () => {
        if (!currentLevel) return '';
        const desc = purpose === 'professional' ? currentLevel.professionalDescription : currentLevel.hobbyDescription;
        return desc;
    };

    // Check if AI summary feature is available for this topic
    const checkSummaryAvailability = useCallback(async () => {
        if (!topic || !authenticatedFetch) {
            return;
        }

        try {
            const response = await authenticatedFetch(API_ENDPOINTS.LEARNING_SUMMARY(topic.id), {
                method: 'POST',
                body: JSON.stringify({
                    userGoals: description,
                    knowledgeLevel: userScale,
                    learningPurpose: purpose,
                    contentLanguage: language,
                    uiLanguage: uiLanguage,
                    checkOnly: true  // This is just a check, don't actually generate
                })
            });
            
            if (!response.ok) {
                return; // Don't set unavailable reason for general errors
            }
            
            const data = await response.json();
            
            if (data && data.error === 'INSUFFICIENT_READ_TIPS') {
                const translatedMessage = (t('insufficientReadTips') || 'You need at least 5 read tips to generate an AI learning summary. You currently have {count} read tip(s). Continue reading and marking tips to unlock this feature.')
                    .replace('{count}', data.readTipsCount || 0);
                setSummaryUnavailableReason(translatedMessage);
            } else if (data && data.available) {
                setSummaryUnavailableReason(null); // Available
            } else {
                setSummaryUnavailableReason(null); // Default to available for other responses
            }
        } catch (error) {
            // Don't set unavailable reason for network errors
            console.log('Could not check summary availability:', error);
        }
    }, [topic, authenticatedFetch, description, userScale, purpose, language, uiLanguage, t]);

    // Generate learning summary using backend API
    const generateLearningSummary = async () => {
        
        // Prevent double-clicks
        if (isLocallyGenerating) {
            return;
        }
        
        if (!topic) {
            alert(t('noTopicSelected'));
            return;
        }

        if (!authenticatedFetch || !generateRequestId || !addPendingRequest || !removePendingRequest) {
            alert(t('authenticationRequired'));
            return;
        }

        // Set local generating state immediately
        setIsLocallyGenerating(true);

        // Create request ID for tracking
        const requestId = generateRequestId('learning-summary', topic.id);
        
        // Clear any existing messages and set local state for immediate UI feedback
        setSummaryMessage(null);
        setLocalGenerationComplete(false);
        addPendingRequest(requestId);

        try {
            
            const response = await authenticatedFetch(API_ENDPOINTS.LEARNING_SUMMARY(topic.id), {
                method: 'POST',
                body: JSON.stringify({
                    userGoals: description,
                    knowledgeLevel: userScale,
                    learningPurpose: purpose,
                    contentLanguage: language,
                    uiLanguage: uiLanguage
                    // Note: checkOnly is NOT included here, so this will actually generate
                })
            });
            
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            
            if (data && data.error === 'INSUFFICIENT_READ_TIPS') {
                const translatedMessage = (t('insufficientReadTips') || 'You need at least 5 read tips to generate an AI learning summary. You currently have {count} read tip(s). Continue reading and marking tips to unlock this feature.')
                    .replace('{count}', data.readTipsCount || 0);
                setSummaryUnavailableReason(translatedMessage);
                removePendingRequest(requestId); // Remove from pending requests
                return; // Don't show error alert for this case
            } else if (data && data.summary) {
                const newSummary = data.summary.trim();
                setLearningSummary(newSummary);
                setLocallyGeneratedSummary(newSummary); // Track locally generated summary
                setLocalGenerationComplete(true); // Enable button immediately
                
                // Show appropriate message based on cache status
                if (data.fromCache) {
                    setSummaryMessage({
                        type: 'info',
                        text: t('summaryCachedMessage') || data.message || 'Your learning context hasn\'t changed since the last summary was generated. Returning the existing analysis.'
                    });
                    // For cached responses, backend already cleared the flag, no need to update topic
                } else {
                    setSummaryMessage({
                        type: 'success',
                        text: t('summaryNewMessage') || data.message || 'New learning summary generated based on your latest activity and interactions.'
                    });
                    
                    // Don't call onUpdateTopic immediately as it causes modal to close
                    // Let the polling system handle the topic update naturally
                }
                
                
                // Message stays visible until manually cleared (no auto-timeout)
            } else {
                throw new Error('No summary returned from service');
            }
        } catch (error) {
            setLocalGenerationComplete(true); // Enable button on error so user can retry
            alert(t('summaryGenerationError') || 'Failed to generate learning summary. Please try again.');
        } finally {
            // Remove from pending requests
            removePendingRequest(requestId);
            setIsLocallyGenerating(false);
        }
    };

    // Tab configuration
    const tabs = [
        { id: 0, label: t('learningDetails') || 'Learning Details' },
        { id: 1, label: t('knowledgeContext') || 'Knowledge & Context' },
        { id: 2, label: t('settings') || 'Settings' }
    ];

    const knowledgeSubTabs = [
        { id: 0, label: t('learningContextGoals') || 'Learning Context & Goals (Optional)' },
        { id: 1, label: t('learningSummary') || 'AI Learning Summary' }
    ];

    // Track previous open state to detect when modal is being opened
    const [wasOpen, setWasOpen] = useState(false);
    // Track if user has modified description to prevent overwriting during typing
    const [hasUserModifiedDescription, setHasUserModifiedDescription] = useState(false);
    
    // Get a random topic example when modal opens
    useEffect(() => {
        if (topic && isOpen) {
            setTopicExample(getRandomTopicExample(uiLanguage));
        }
    }, [isOpen, uiLanguage, topic]);

    useEffect(() => {
        if (topic && isOpen) {
            console.log('[EditTopicModal] Topic data:', topic);
            console.log('[EditTopicModal] nextDueTime:', topic.nextDueTime);
            console.log('[EditTopicModal] isScheduled:', topic.isScheduled);
            setTopicName(topic.name || '');
            // Use explicit check to preserve 0 (beginner level)
            const savedKnowledgeLevel = (topic.knowledgeLevel !== undefined && topic.knowledgeLevel !== null) 
                ? topic.knowledgeLevel 
                : (topic.knowledge_level !== undefined && topic.knowledge_level !== null) 
                    ? topic.knowledge_level 
                    : (topic.userScale !== undefined && topic.userScale !== null) 
                        ? topic.userScale 
                        : 3;
            setUserScale(savedKnowledgeLevel);
            setPurpose(topic.learningPurpose || topic.purpose || 'hobby');
            setLanguage(topic.contentLanguage || topic.language || getDefaultLanguage(uiLanguage));
            
            // Only update description if user hasn't modified it or if modal is being opened for the first time
            if (!hasUserModifiedDescription || !wasOpen) {
                setDescription(topic.description || '');
                setHasUserModifiedDescription(false);
            }
            
            setIncludeSummaryInContext(topic.includeSummaryInContext !== undefined ? topic.includeSummaryInContext : true);
            setLearningSummary(topic.learningSummary || ''); // Set initial learning summary when modal opens
            setSummaryUnavailableReason(null); // Reset when opening with a topic
            // Only reset summary message when modal is being opened (transitioning from closed to open)
            if (!wasOpen && isOpen) {
                setSummaryMessage(null); // Reset summary message only on initial open
                setLocalGenerationComplete(false); // Reset local generation state
                setLocallyGeneratedSummary(null); // Reset locally generated summary
                setIsLocallyGenerating(false); // Reset local generating state
                
                // Set active tab based on tutorial step if in tutorial mode
                if (tutorialStepId === 'scheduled-generation') {
                    setActiveTab(2); // Settings tab for scheduled generation
                    setKnowledgeSubTab(0);
                } else if (tutorialStepId === 'ai-learning-summary') {
                    setActiveTab(1); // Knowledge & Context tab
                    setKnowledgeSubTab(1); // AI Learning Summary sub-tab
                } else {
                    setActiveTab(0); // Start with the first tab when opening normally
                    setKnowledgeSubTab(0); // Start with the first sub-tab when opening normally
                }
            }
            setIsScheduled(topic.isScheduled || false);
            // Use explicit check to preserve 0 (12 AM)
            const savedTimeHour = (topic.scheduleTimeHour !== undefined && topic.scheduleTimeHour !== null) 
                ? topic.scheduleTimeHour 
                : 7;
            setScheduleTimeHour(savedTimeHour);
            // Use explicit check to preserve 0 (AM)
            const savedTimePeriod = (topic.scheduleTimePeriod !== undefined && topic.scheduleTimePeriod !== null) 
                ? topic.scheduleTimePeriod 
                : 0;
            setScheduleTimePeriod(savedTimePeriod);
            setScheduleFrequencyValue(topic.scheduleFrequencyValue || 1);
            
            // Use explicit check to preserve 0 (hours)
            const savedFrequencyUnit = (topic.scheduleFrequencyUnit !== undefined && topic.scheduleFrequencyUnit !== null)
                ? topic.scheduleFrequencyUnit
                : 1;
            
            if (savedFrequencyUnit === 0 && process.env.NODE_ENV === 'production') {
                setScheduleFrequencyUnit(1); // Default to days in production
                setWasHoursInDev(true);
            } else {
                setScheduleFrequencyUnit(savedFrequencyUnit);
                setWasHoursInDev(false);
            }
            
            setScheduledSkipIfUnread(topic.scheduledSkipIfUnread || false);
            
            // Handle timezone: always use current browser timezone, but track saved timezone for comparison
            const currentTimezone = timezone;
            const savedTimezoneValue = topic.userTimezone || currentTimezone;
            
            setSavedTimezone(savedTimezoneValue);
            setUserTimezone(currentTimezone);
            
            // Show alert if current timezone differs from saved timezone
            // Only show alert when opening the modal, not when user manually changes selection
            setShowTimezoneAlert(savedTimezoneValue !== currentTimezone);

            // Check if AI summary feature is available
            checkSummaryAvailability();
        }
        
        // Update wasOpen state
        setWasOpen(isOpen);
    }, [topic, uiLanguage, isOpen, timezone, checkSummaryAvailability, wasOpen, hasUserModifiedDescription, tutorialStepId]);

    // Separate effect to handle tutorial step changes when modal is already open
    useEffect(() => {
        if (isOpen && tutorialStepId) {
            if (tutorialStepId === 'scheduled-generation') {
                setActiveTab(2); // Settings tab for scheduled generation
                setKnowledgeSubTab(0);
            } else if (tutorialStepId === 'ai-learning-summary') {
                setActiveTab(1); // Knowledge & Context tab
                setKnowledgeSubTab(1); // AI Learning Summary sub-tab
            }
        }
    }, [tutorialStepId, isOpen]);

    // Separate effect to handle learning summary updates during polling
    // This ensures we only update the summary if it's for the currently displayed topic
    const [currentTopicId, setCurrentTopicId] = useState(null);
    
    useEffect(() => {
        if (topic?.id && isOpen) {
            setCurrentTopicId(topic.id);
        }
    }, [topic?.id, isOpen]);
    
    useEffect(() => {
        // Only update learning summary if the topic matches the currently displayed one
        if (topic?.id && isOpen && topic.id === currentTopicId) {
            // If we have a locally generated summary and the props summary hasn't caught up yet,
            // don't overwrite the local summary
            if (locallyGeneratedSummary && topic.learningSummary !== locallyGeneratedSummary) {
                // Props haven't updated yet, keep the locally generated summary
                return;
            }
            
            // If props have caught up with our locally generated summary, clear the local tracking
            if (locallyGeneratedSummary && topic.learningSummary === locallyGeneratedSummary) {
                setLocallyGeneratedSummary(null);
            }
            
            // Update the learning summary from props
            setLearningSummary(topic.learningSummary || '');
            
            // If we have a summary and the backend says we're not generating anymore,
            // ensure localGenerationComplete is true to enable the button
            if (topic.learningSummary && !topic.isGeneratingSummary && isGeneratingSummary) {
                setLocalGenerationComplete(true);
            }
        }
    }, [topic?.learningSummary, topic?.id, topic?.name, topic?.isGeneratingSummary, isOpen, currentTopicId, isGeneratingSummary, locallyGeneratedSummary]);

    // Reset form when modal is closed and no topic is selected
    useEffect(() => {
        if (!isOpen && !topic) {
            setTopicName('');
            setUserScale(3);
            setPurpose('hobby');
            setLanguage(getDefaultLanguage(uiLanguage));
            setDescription('');
            setLearningSummary('');
            setIncludeSummaryInContext(true);
            setSummaryUnavailableReason(null);
            setSummaryMessage(null);
            setLocalGenerationComplete(false);
            setLocallyGeneratedSummary(null);
            setIsLocallyGenerating(false);
            setActiveTab(0);
            setKnowledgeSubTab(0);
            setShowDeleteConfirm(false);
            setIsScheduled(false);
            setScheduleTimeHour(7);
            setScheduleTimePeriod(0);
            setScheduleFrequencyValue(1);
            setScheduleFrequencyUnit(1);
            setScheduledSkipIfUnread(false);
            setUserTimezone(timezone);
            setSavedTimezone(null);
            setShowTimezoneAlert(false);
            setHasUserModifiedDescription(false);
        }
    }, [isOpen, topic, uiLanguage, timezone]);

    if (!isOpen || !topic) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (topicName.trim()) {
            const updateData = {
                name: topicName.trim(),
                knowledgeLevel: userScale,
                learningPurpose: purpose,
                contentLanguage: language,
                description: description.trim(),
                learningSummary: learningSummary.trim(),
                includeSummaryInContext: includeSummaryInContext,
                isScheduled: isScheduled,
                scheduleTimeHour: scheduleTimeHour,
                scheduleTimePeriod: scheduleTimePeriod,
                scheduleFrequencyValue: scheduleFrequencyValue,
                scheduleFrequencyUnit: scheduleFrequencyUnit,
                scheduledSkipIfUnread: scheduledSkipIfUnread,
                userTimezone: userTimezone
            };
            onUpdateTopic(topic.id, updateData);
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4"
            onMouseDown={handleBackdropClick}
        >
            <div 
                className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl animate-fade-in-up"
                data-tutorial-target="edit-topic-modal"
            >
                <h2 className="text-xl font-semibold mb-4 text-gray-800">{t('editTopic')}</h2>
                
                {/* Tab Navigation */}
                <div className="flex border-b border-gray-200 mb-6">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === tab.id
                                    ? 'border-sky-500 text-sky-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>

                <form onSubmit={handleSubmit}>
                    
                    {/* Tab Content */}
                    {activeTab === 0 && (
                        <div className="space-y-6">
                            {/* Topic Name */}
                            <div>
                                <label className="text-sm font-bold text-gray-600 block mb-2">{t('topicName')}</label>
                                <input
                                    type="text"
                                    autoFocus
                                    value={topicName}
                                    onChange={(e) => setTopicName(e.target.value)}
                                    placeholder={topicExample ? `e.g., ${topicExample.name}` : t('topicNamePlaceholder')}
                                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                    autoComplete="off"
                                />
                            </div>

                            {/* Learning Goal */}
                            <div>
                                <label className="text-sm font-bold text-gray-600 block mb-3">{t('learningGoalQuestion')}</label>
                                <div className="flex gap-4">
                                    {learningPurposes.map(p => (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => setPurpose(p.id)}
                                            className={`w-full p-3 rounded-md border text-sm transition-colors ${purpose === p.id ? 'bg-sky-500 text-white border-sky-500' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                                        >
                                            {t(p.labelKey)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Language Selection */}
                            <div>
                                <label className="text-sm font-bold text-gray-600 block mb-2">{t('contentLanguageQuestion')}</label>
                                <select
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                    className="w-full p-3 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                                >
                                    {languageOptions.map(lang => (
                                        <option key={lang.id} value={lang.id}>{lang.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {activeTab === 1 && (
                        <div className="h-full flex flex-col">
                            {/* Knowledge Scale */}
                            <div className="mb-6">
                                <label className="text-sm font-bold text-gray-600 block mb-3">{t('rateYourKnowledge')}</label>
                                <input
                                    type="range"
                                    min="0" max="10" step="1"
                                    value={userScale}
                                    onChange={(e) => setUserScale(Number(e.target.value))}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                />
                                <div className="flex justify-between text-xs text-gray-500 px-1 mt-1">
                                    {Array.from(Array(11).keys()).map(i => <span key={i}>{i}</span>)}
                                </div>
                                <div className="mt-3 text-center p-3 bg-sky-50 rounded-md border border-sky-200 min-h-[60px]">
                                    <span className="font-bold text-sky-800">{currentLevel.label} (Level {currentLevel.scale})</span>
                                    <p className="text-sm text-sky-700">{getCurrentDescription()}</p>
                                </div>
                            </div>

                            {/* Sub-Tab Navigation */}
                            <div className="flex justify-between items-center border-b border-gray-200 mb-4">
                                <div className="flex">
                                    {knowledgeSubTabs.map((subTab) => (
                                        <button
                                            key={subTab.id}
                                            type="button"
                                            onClick={() => setKnowledgeSubTab(subTab.id)}
                                            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                                                knowledgeSubTab === subTab.id
                                                    ? 'border-sky-500 text-sky-600'
                                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            }`}
                                        >
                                                            <span>{subTab.label}</span>
                                        </button>
                                    ))}
                                </div>
                                
                                {/* Generate Summary Button - only show when AI Learning Summary tab is active */}
                                {knowledgeSubTab === 1 && (
                                    <button
                                        type="button"
                                        onClick={generateLearningSummary}
                                        disabled={isLocallyGenerating || (isGeneratingSummary && !localGenerationComplete) || summaryUnavailableReason}
                                        className={`px-3 py-1 text-xs rounded flex items-center gap-1 ${
                                            summaryUnavailableReason 
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                                : (isLocallyGenerating || (isGeneratingSummary && !localGenerationComplete))
                                                    ? 'bg-gray-300 text-white cursor-not-allowed'
                                                    : 'bg-sky-500 text-white hover:bg-sky-600'
                                        }`}
                                        title={summaryUnavailableReason || (summaryMessage?.type === 'info' ? t('forceRegenerateTooltip') || 'Click again to force regenerate' : '')}
                                    >
                                        {(isLocallyGenerating || (isGeneratingSummary && !localGenerationComplete)) ? (
                                            <>
                                                <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                                                {t('generating') || 'Generating...'}
                                            </>
                                        ) : (
                                            <>
                                                {t('generateSummary') || 'Generate Summary'}
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>

                            {/* Sub-Tab Content */}
                            <div className="flex-1 overflow-y-auto">
                                {knowledgeSubTab === 0 && (
                                    <div>
                                        {/* Learning Context & Goals */}
                                        <div>
                                            <textarea
                                                value={description}
                                                onChange={(e) => {
                                                    setDescription(e.target.value);
                                                    setHasUserModifiedDescription(true);
                                                }}
                                                rows="8"
                                                maxLength={500}
                                                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                                                placeholder={t('learningContextPlaceholder') || 'Describe your specific learning goals, interests, or what you want to focus on...'}
                                            />
                                            <div className="flex justify-between items-center mt-1">
                                                <p className="text-xs text-gray-500">
                                                    {t('learningContextHelp')}
                                                </p>
                                                <span className={`text-xs ${description.length >= 450 ? 'text-orange-600' : 'text-gray-500'}`}>
                                                    {description.length}/500
                                                </span>
                                            </div>
                                            {topicExample && (
                                                <div className="text-xs text-gray-400 mt-2 p-2 bg-gray-50 rounded border">
                                                    <strong>{t('example')}:</strong> "{topicExample.description}"
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {knowledgeSubTab === 1 && (
                                    <div>
                                        {/* AI Learning Summary Section */}
                                        
                                        {!learningSummary.trim() ? (
                                            <div className="w-full p-3 border border-gray-300 rounded-md text-gray-500 bg-gray-50 min-h-[8rem] flex items-center justify-center">
                                                {t('summaryPlaceholder') || 'Click "Generate Summary" to analyze your learning patterns and interests...'}
                                            </div>
                                        ) : (
                                            <div className="w-full p-3 border border-gray-300 rounded-md bg-gray-50 min-h-[12rem] max-h-80 overflow-y-auto">
                                                {renderMarkdown(learningSummary)}
                                            </div>
                                        )}
                                        
                                        {/* Summary generation message */}
                                        {summaryMessage && (
                                            <div className={`mt-3 p-3 rounded-md border ${
                                                summaryMessage.type === 'success' 
                                                    ? 'bg-green-50 border-green-200 text-green-800' 
                                                    : summaryMessage.type === 'info'
                                                        ? 'bg-blue-50 border-blue-200 text-blue-800'
                                                        : 'bg-gray-50 border-gray-200 text-gray-800'
                                            }`}>
                                                <span className="text-sm">{summaryMessage.text}</span>
                                            </div>
                                        )}
                                        
                                        <div className="flex justify-between items-center mt-1">
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id="includeSummaryInContext"
                                                    checked={includeSummaryInContext}
                                                    onChange={(e) => setIncludeSummaryInContext(e.target.checked)}
                                                    className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                                                />
                                                <label htmlFor="includeSummaryInContext" className="text-xs text-gray-600 cursor-pointer">
                                                    {t('includeSummaryInPrompt') || 'Include in AI context for personalized content'}
                                                </label>
                                            </div>
                                        </div>
                                        
                                        <p className="text-xs text-gray-500 mt-2">
                                            {t('summaryHelp') || 'This AI-generated summary analyzes your learning patterns to personalize future content. It\'s editable and can be included in the context for better AI responses.'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 2 && (
                        <div className="space-y-6">
                            {/* Scheduling Controls */}
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center gap-3 mb-4">
                                    <input
                                        type="checkbox"
                                        id="isScheduled"
                                        checked={isScheduled}
                                        onChange={(e) => setIsScheduled(e.target.checked)}
                                        className="h-5 w-5 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="isScheduled" className="text-sm font-bold text-gray-700 cursor-pointer">
                                        {t('enableAutomaticTipGeneration')}
                                    </label>
                                </div>
                        
                        {isScheduled && (
                            <div className="ml-8 space-y-4">
                                {/* Time of day */}
                                <div>
                                    <label className="text-sm font-medium text-gray-600 block mb-2">
                                        {t('generateTipsAt')}
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            min="1"
                                            max="12"
                                            value={scheduleTimeHour}
                                            onChange={(e) => setScheduleTimeHour(Math.max(1, Math.min(12, parseInt(e.target.value) || 4)))}
                                            className="w-16 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                        />
                                        <select
                                            value={scheduleTimePeriod}
                                            onChange={(e) => setScheduleTimePeriod(parseInt(e.target.value))}
                                            className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                        >
                                            <option value={0}>AM</option>
                                            <option value={1}>PM</option>
                                        </select>
                                    </div>
                                </div>
                                
                                {/* Frequency */}
                                <div>
                                    <label className="text-sm font-medium text-gray-600 block mb-2">
                                        {t('generateNewTipsEvery')}
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            min="1"
                                            max="30"
                                            value={scheduleFrequencyValue}
                                            onChange={(e) => setScheduleFrequencyValue(Math.max(1, Math.min(30, parseInt(e.target.value) || 1)))}
                                            className="w-16 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                        />
                                        <select
                                            value={scheduleFrequencyUnit}
                                            onChange={(e) => setScheduleFrequencyUnit(parseInt(e.target.value))}
                                            className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                        >
                                            {process.env.NODE_ENV !== 'production' && (
                                                <option value={0}>{t('hours')}</option>
                                            )}
                                            <option value={1}>{t('days')}</option>
                                            <option value={2}>{t('weeks')}</option>
                                            <option value={3}>{t('months')}</option>
                                        </select>
                                    </div>
                                    {wasHoursInDev && (
                                        <div className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                                            {uiLanguage === 'zh-CN' ? (
                                                <>注意：此主题的计划频率实际上设置为"小时"（来自先前环境）。如需更改为天、周或月，请重新保存。</>
                                            ) : uiLanguage === 'ja' ? (
                                                <>注意: このトピックのスケジュールは実際には「時間」単位で設定されています（以前の環境から）。日・週・月に変更したい場合は再度保存してください。</>
                                            ) : (
                                                <>Note: This topic's schedule is actually set to <b>hours</b> (from a previous environment). Please update and save if you want to use days, weeks, or months.</>
                                            )}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Timezone Selection */}
                                <div>
                                    <label className="text-sm font-medium text-gray-600 block mb-2">
                                        {t('timezone')}
                                    </label>
                                    <select
                                        value={userTimezone}
                                        onChange={(e) => {
                                            const newTimezone = parseInt(e.target.value);
                                            setUserTimezone(newTimezone);
                                            // Hide alert if user selects the same timezone as saved
                                            if (newTimezone === savedTimezone) {
                                                setShowTimezoneAlert(false);
                                            }
                                        }}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                    >
                                        {getTimezoneOptions(uiLanguage).map(timezone => (
                                            <option key={timezone.value} value={timezone.value}>
                                                {timezone.label}
                                            </option>
                                        ))}
                                    </select>
                                    {showTimezoneAlert && (
                                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                                            <p className="text-xs text-amber-800">
                                                {uiLanguage === 'zh-CN' ? 
                                                    `检测到时区变化：从 ${getTimezoneName(savedTimezone, uiLanguage)} 更改为 ${getTimezoneName(userTimezone, uiLanguage)}` :
                                                    uiLanguage === 'ja' ? 
                                                    `タイムゾーンの変更を検出：${getTimezoneName(savedTimezone, uiLanguage)} から ${getTimezoneName(userTimezone, uiLanguage)} に変更` :
                                                    `Timezone change detected: from ${getTimezoneName(savedTimezone, uiLanguage)} to ${getTimezoneName(userTimezone, uiLanguage)}`
                                                }
                                            </p>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Next scheduled time display */}
                                {isScheduled && topic.nextDueTime && (
                                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                        <p className="text-sm text-blue-800">
                                            <span className="font-medium">
                                                {uiLanguage === 'zh-CN' ? '下次提示生成时间：' :
                                                 uiLanguage === 'ja' ? '次回のヒント生成時刻：' :
                                                 'Next tip generation:'}
                                            </span>
                                            <span className="ml-1">
                                                {new Date(topic.nextDueTime).toLocaleString(
                                                    uiLanguage === 'zh-CN' ? 'zh-CN' : uiLanguage === 'ja' ? 'ja-JP' : 'en-US',
                                                    {
                                                        weekday: 'short',
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: 'numeric',
                                                        minute: '2-digit',
                                                        hour12: true
                                                    }
                                                )}
                                            </span>
                                        </p>
                                    </div>
                                )}
                                
                                <p className="text-xs text-gray-500">
                                    {t('schedulingNote')}
                                </p>
                                <div className="flex items-center gap-2 mt-4">
                                    <input
                                        type="checkbox"
                                        id="scheduledSkipIfUnread"
                                        checked={scheduledSkipIfUnread}
                                        onChange={e => setScheduledSkipIfUnread(e.target.checked)}
                                        className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="scheduledSkipIfUnread" className="text-xs text-gray-700 cursor-pointer">
                                        {t('scheduledSkipIfUnread')}
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>
                        </div>
                    )}

                    {/* Reminder about changes */}
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                        <p className="text-sm text-amber-800">
                            {t('editTopicChangesNote')}
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-between items-center mt-6">
                        <button 
                            type="button" 
                            onClick={() => setShowDeleteConfirm(true)} 
                            className="px-4 py-2 rounded-md text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors"
                        >
                            {t('deleteConfirmTitle')}
                        </button>
                        <div className="flex gap-4">
                            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200">{t('cancel')}</button>
                            <button type="submit" disabled={!topicName.trim()} className="px-4 py-2 rounded-md text-white bg-sky-500 hover:bg-sky-600 disabled:bg-gray-300">{t('saveChanges')}</button>
                        </div>
                    </div>
                </form>
                
                {/* Delete Confirmation Dialog */}
                {showDeleteConfirm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
                        <div className="bg-white rounded-lg p-6 max-w-md shadow-xl" onMouseDown={e => e.stopPropagation()}>
                            <h3 className="text-lg font-semibold mb-4 text-gray-800">{t('deleteConfirmTitle')}</h3>
                            <p className="text-gray-600 mb-6">
                                {(() => {
                                    const titleToUse = topicName || topic.name;
                                    
                                    // Simple conditional based on UI language
                                    let message;
                                    if (uiLanguage === 'zh-CN') {
                                        message = (
                                            <>
                                                您确定要删除主题{' '}
                                                <span className="bg-gray-100 px-2 py-1 rounded font-semibold text-gray-800">
                                                    {titleToUse}
                                                </span>
                                                吗？此操作无法撤销。
                                            </>
                                        );
                                    } else if (uiLanguage === 'ja') {
                                        message = (
                                            <>
                                                トピック「
                                                <span className="bg-gray-100 px-2 py-1 rounded font-semibold text-gray-800">
                                                    {titleToUse}
                                                </span>
                                                」を削除してもよろしいですか？この操作は元に戻せません。
                                            </>
                                        );
                                    } else {
                                        message = (
                                            <>
                                                Are you sure you want to delete the topic{' '}
                                                <span className="bg-gray-100 px-2 py-1 rounded font-semibold text-gray-800">
                                                    {titleToUse}
                                                </span>
                                                ? This action cannot be undone.
                                            </>
                                        );
                                    }
                                    
                                    return message;
                                })()}
                            </p>
                            <div className="flex justify-end gap-4">
                                <button 
                                    onClick={() => setShowDeleteConfirm(false)} 
                                    className="px-4 py-2 rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200"
                                >
                                    {t('cancel')}
                                </button>
                                <button 
                                    onClick={() => {
                                        onDeleteTopic(topic.id);
                                        setShowDeleteConfirm(false);
                                        onClose();
                                    }} 
                                    className="px-4 py-2 rounded-md text-white bg-red-500 hover:bg-red-600"
                                >
                                    {t('delete')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
